import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import styled from 'styled-components';

import { BOLINDA_ICON_LIBX, MATERIAL_ICON_CAMPAIGNS, MATERIAL_ICON_LANGUAGE } from '../common/Button/Icon';
import {
    getConcreteLoanFormatStates,
    isConcreteLoanFormatObject,
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EPRESS_OBJECT
} from '../../store/loanFormat/actions';
import Utils from '../utils';
import { textColor, THEME_PRIMARY } from '../../styles/basics/colors';
import { CAMPAIGN_PRODUCTS, MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import NavigationDropDownLink from '../components/NavigationDropDownLink';
import { LoanFormatState } from '../../store/loanFormat/state';
import { AppState } from '../../store/appState';
import { DropDownMenu } from '../common/DropDownMenu';
import { matchPath, useLocation } from 'react-router';
import { LoanFormatIcon } from '../common/GildIcon';
import { useTranslation } from 'react-i18next';
import { mapLoanFormat } from '../../../models/LoanFormat';

const StIcon = styled.i`
    font-size: inherit;
    line-height: 1 !important;
    margin-left: 2px;
`;

const StCheckIcon = styled(StIcon)`
    font-size: 22px;
    ${textColor(THEME_PRIMARY)};
    position: absolute;
    top: 0;
    right: 5px;
`;

const StLabel = styled.span`
    margin-left: 0.3em;
    margin-right: 15px;
`;

type OwnProps = {
    readonly locationPathname?: string;
    readonly isCampaigns?: boolean;
    readonly isLibx?: boolean;
    readonly isForeignLanguage?: boolean;
    readonly className?: string;
    readonly shopLanguage?: string;
};

type Props = {
    readonly loanFormat: LoanFormatState;
};

const TitleDropdown: FC<Props & OwnProps> = props => {
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation();
    const campaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    const loanFormats = getConcreteLoanFormatStates();

    const config = useTitleDropdownConfig();

    if (!config) {
        return null;
    }

    const { label, iconCode, iconDefaultClasses } = config;
    return (
        <DropDownMenu
            expanded={expanded}
            onToggleClick={setExpanded}
            onClickOutside={setExpanded}
            align="left"
            className={classNames('dd-loanformat', 'fl-none', 'display-flex', props.className)}
            label={label}
            iconCode={iconCode}
            iconDefaultClasses={iconDefaultClasses}
            iconSizeClass={!props.isLibx ? undefined : 'uppest'}
            dataDisplayname="TitleDropdown"
        >
            {loanFormats.map(loanFormat => {
                const loanFormatName = mapLoanFormat(loanFormat.name)?.toLowerCase();
                let pathname;
                let loanClasses;
                let check;
                if (isConcreteLoanFormatObject(props.loanFormat) && props.locationPathname) {
                    const loanFormatKey = loanFormat.key.toLowerCase();
                    const currentLoanFormatKey = props.loanFormat.key.toLowerCase();
                    pathname = props.locationPathname.replace(currentLoanFormatKey, loanFormatKey);
                    const isActive = loanFormat.key === props.loanFormat.key;
                    loanClasses = classNames('display-flex', {
                        checked: isActive
                    });
                    check = isActive ? <i className="material-icons">check</i> : null;
                }
                const icon = <LoanFormatIcon icon={loanFormat.icon} className="loanformat-icon-medium" />;
                loanClasses = loanClasses || 'display-flex';
                pathname = pathname || Utils.buildUrl('catalogue', loanFormat.key.toLowerCase(), 'featured');
                return (
                    <li key={loanFormat.key}>
                        <NavigationDropDownLink
                            to={pathname}
                            className={loanClasses}
                            onClick={() => setExpanded(false)}
                        >
                            {icon}
                            <span>
                                {t(`loanFormat.plain.${loanFormatName}`, {
                                    count: 10
                                })}
                            </span>
                            {check}
                        </NavigationDropDownLink>
                    </li>
                );
            })}

            {isMultiLanguageShopEnabled ? (
                <li>
                    <NavigationDropDownLink
                        to={`/shop/${props.shopLanguage}/languagelearning`}
                        className="display-flex"
                    >
                        <StIcon className="material-icons">language</StIcon>
                        <StLabel>{t('menu.shop.languages.languageLearning')}</StLabel>
                        {props.isForeignLanguage ? <StCheckIcon className="material-icons">check</StCheckIcon> : null}
                    </NavigationDropDownLink>
                </li>
            ) : (
                <li>
                    <NavigationDropDownLink to="/language" className="display-flex">
                        <StIcon className="material-icons">language</StIcon>
                        <StLabel>{t('menu.shop.languageMain')}</StLabel>
                        {props.isForeignLanguage ? <StCheckIcon className="material-icons">check</StCheckIcon> : null}
                    </NavigationDropDownLink>
                </li>
            )}
            {campaignsEnabled ? (
                isMultiLanguageShopEnabled ? (
                    <li>
                        <NavigationDropDownLink to={`/shop/${props.shopLanguage}/unlimited`} className="display-flex">
                            <StIcon className="material-icons">{MATERIAL_ICON_CAMPAIGNS}</StIcon>
                            <StLabel>{t('menu.shop.unlimitedMain')}</StLabel>
                            {props.isCampaigns ? <StCheckIcon className="material-icons">check</StCheckIcon> : null}
                        </NavigationDropDownLink>
                    </li>
                ) : (
                    <li>
                        <NavigationDropDownLink to="/unlimited" className="display-flex">
                            <StIcon className="material-icons">{MATERIAL_ICON_CAMPAIGNS}</StIcon>
                            <StLabel>{t('menu.shop.unlimitedMain')}</StLabel>
                            {props.isCampaigns ? <StCheckIcon className="material-icons">check</StCheckIcon> : null}
                        </NavigationDropDownLink>
                    </li>
                )
            ) : null}
            {isMultiLanguageShopEnabled ? (
                <li>
                    <NavigationDropDownLink to={`/shop/${props.shopLanguage}/libxchange`} className="display-flex">
                        <StIcon className="bolinda-icons">{BOLINDA_ICON_LIBX}</StIcon>
                        <StLabel>{t('menu.shop.libxchangeMain')}</StLabel>
                        {props.isLibx ? <StCheckIcon className="material-icons">check</StCheckIcon> : null}
                    </NavigationDropDownLink>
                </li>
            ) : (
                <li>
                    <NavigationDropDownLink to="/libxchange" className="display-flex">
                        <StIcon className="bolinda-icons">{BOLINDA_ICON_LIBX}</StIcon>
                        <StLabel>{t('menu.shop.libxchangeMain')}</StLabel>
                        {props.isLibx ? <StCheckIcon className="material-icons">check</StCheckIcon> : null}
                    </NavigationDropDownLink>
                </li>
            )}
        </DropDownMenu>
    );
};

function mapStateToProps(state: AppState): Props {
    return {
        loanFormat: state.loanFormat
    };
}

export default connect(mapStateToProps)(TitleDropdown);

type UseTitleDropdownConfig =
    | {
          readonly label: string;
          readonly iconCode: string;
          readonly iconDefaultClasses?: string;
      }
    | undefined;

function useTitleDropdownConfig(): UseTitleDropdownConfig {
    const { pathname } = useLocation();
    const { t } = useTranslation();

    const libxMatch = matchPath(pathname, '/libxchange');
    const shopLibxMatch = matchPath(pathname, '/shop/:shopLanguage/libxchange');
    if (libxMatch || shopLibxMatch) {
        return {
            label: t('menu.shop.libxchangeMain'),
            iconCode: BOLINDA_ICON_LIBX,
            iconDefaultClasses: 'bolinda-icons'
        };
    }

    const languagesMatch = matchPath(pathname, '/language');
    if (languagesMatch) {
        return {
            label: t('menu.shop.languageMain'),
            iconCode: MATERIAL_ICON_LANGUAGE,
            iconDefaultClasses: 'material-icons material-label-icon'
        };
    }

    const shopLanguageLearningMatch = matchPath(pathname, '/shop/:shopLanguage/languagelearning');
    if (shopLanguageLearningMatch) {
        return {
            label: t('menu.shop.languages.languageLearning'),
            iconCode: MATERIAL_ICON_LANGUAGE,
            iconDefaultClasses: 'material-icons material-label-icon'
        };
    }

    const unlimitedMatch = matchPath(pathname, '/unlimited');
    const shopUnlimitedMatch = matchPath(pathname, '/shop/:shopLanguage/unlimited');
    if (unlimitedMatch || shopUnlimitedMatch) {
        return {
            label: t('menu.shop.unlimitedMain'),
            iconCode: MATERIAL_ICON_CAMPAIGNS,
            iconDefaultClasses: 'material-icons material-label-icon'
        };
    }

    const catalogueMatch = matchPath<{ loanFormatKey: string }>(pathname, '/catalogue/:loanFormatKey/:page');
    const shopMatch = matchPath<{ loanFormatKey: string }>(pathname, '/shop/:shopLanguage/:loanFormatKey/:page');
    if (catalogueMatch || shopMatch) {
        const loanFormatKeyCatalogue = catalogueMatch?.params.loanFormatKey.toLowerCase();
        const loanFormatKeyShop = shopMatch?.params.loanFormatKey.toLowerCase();

        switch (loanFormatKeyCatalogue ?? loanFormatKeyShop) {
            case 'ebooks':
                return {
                    label: t('loanFormat.plain.ebooks', { count: 10 }),
                    iconCode: LOANFORMAT_EBOOK_OBJECT.icon
                };
            case 'eaudiobooks':
                return {
                    label: t('loanFormat.plain.eaudiobooks', { count: 10 }),
                    iconCode: LOANFORMAT_EAUDIOBOOK_OBJECT.icon
                };
            case 'emagazines':
            case 'epress':
                return {
                    label: t('loanFormat.plain.epress', { count: 10 }),
                    iconCode: LOANFORMAT_EPRESS_OBJECT.icon
                };

            default:
                return undefined;
        }
    }

    return undefined;
}
